import { FC, useCallback, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  InputBase,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'

import { CopyableHeading } from '@/apps/latest/components/organisms/PostRecordingView/CopyableHeading'
import { SectionRenderer } from '@/apps/latest/components/organisms/PostRecordingView/SectionRenderer'
import { selectedText } from '@/apps/latest/components/organisms/PostRecordingView/homeVisitFields'
import { useCopyText } from '@/apps/latest/components/organisms/PostRecordingView/hooks/useSoapCopy'
import { Layout } from '@/apps/latest/components/templates/Layout'
import {
  type CarePlan,
  type CarePlanLetterhead,
} from '@/apps/latest/features/home-visit/carePlanApi'
import { monthDay } from '@/apps/latest/features/home-visit/carePlanDates'
import {
  buildCarePlanPage,
  carePlanDocument,
} from '@/apps/latest/features/home-visit/printCarePlan'
import { printReportPages } from '@/apps/latest/features/home-visit/printHomeVisitReport'
import { RecipientPrintButton } from '@/apps/latest/features/home-visit/RecipientPrintButton'
import { ScheduledDateChips } from '@/apps/latest/features/home-visit/ScheduledDateChips'
import {
  useCarePlanEditing,
  useCarePlans,
  useCreateCarePlan,
  useReviewCarePlan,
  useScheduledDates,
  visibleCarePlanFields,
} from '@/apps/latest/features/home-visit/useCarePlan'
import { useHomecarePatient } from '@/apps/latest/features/home-visit/useHomecare'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { fieldInputSx } from '@/apps/latest/shared/styles/inlineInput'
import { BodyText } from '@/components/atoms/BodyText'
import { MetaText } from '@/components/atoms/MetaText'
import { type SoapContentItem } from '@/shared/types/yakurekiSoap'
import { scaledFontSize } from '@/shared/styles/typography'

const versionLabel = (plan: CarePlan) =>
  `第${plan.version}版 ${monthDay(plan.created_at.slice(0, 10))}`

// 最新の版の文。報告書と同じ部品で、行の編集・採否・見出しのコピーができる
const EditableBody: FC<{ patientId: string; plan: CarePlan }> = ({
  patientId,
  plan,
}) => {
  const editing = useCarePlanEditing(patientId, plan)
  const copyText = useCopyText()
  const copyField = useCallback(
    (label: string, items: SoapContentItem[]) => {
      void copyText(selectedText(items), `${label}をコピーしました`)
    },
    [copyText]
  )
  return (
    <Stack spacing={2}>
      {visibleCarePlanFields(plan).map(({ key, label }) => {
        const items = plan.fields[key] ?? []
        return (
          <SectionRenderer
            key={key}
            sectionKey={key}
            label={label}
            items={items}
            editing={editing}
            strike={{
              isStruck: (item) => !(item.is_selected ?? true),
              toggleLine: (_item, i) => editing.toggleSelect(key, i),
            }}
            onCopy={() => copyField(label, items)}
            copyTitle={`${label}をコピー`}
            medications={[]}
            onPlaceholderReplace={async () => false}
          />
        )
      })}
    </Stack>
  )
}

// 前の版の文。読むだけ (見出しのコピーはできる)
const ReadOnlyBody: FC<{ plan: CarePlan }> = ({ plan }) => {
  const copyText = useCopyText()
  return (
    <Stack spacing={2}>
      {visibleCarePlanFields(plan).map(({ key, label }) => {
        const text = selectedText(plan.fields[key] ?? [])
        return (
          <Box key={key}>
            <CopyableHeading
              label={label}
              title={`${label}をコピー`}
              onCopy={() => void copyText(text, `${label}をコピーしました`)}
              showCopyButton={Boolean(text)}
            />
            {text ? (
              text
                .split('\n')
                .map((line, i) => <BodyText key={i}>{line}</BodyText>)
            ) : (
              <MetaText>(なし)</MetaText>
            )}
          </Box>
        )
      })}
    </Stack>
  )
}

const PlanView: FC<{
  patientId: string
  plans: CarePlan[]
  letterhead: CarePlanLetterhead
  ended: boolean
}> = ({ patientId, plans, letterhead, ended }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const review = useReviewCarePlan(patientId)
  const latest = plans[0]
  const plan = plans.find((p) => p.id === selectedId) ?? latest
  const isLatest = plan.id === latest.id
  const dates = useScheduledDates(patientId, latest)

  return (
    <Stack spacing={2} sx={{ maxWidth: 720 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Select
          size="small"
          value={plan.id}
          onChange={(e) => setSelectedId(e.target.value)}
          input={<InputBase sx={{ ...fieldInputSx, flex: 'none' }} />}
          SelectDisplayProps={{ 'aria-label': '計画書の版' }}
        >
          {plans.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {versionLabel(p)}
            </MenuItem>
          ))}
        </Select>
        <Box sx={{ flexGrow: 1 }} />
        {!ended && (
          <Button
            variant="text"
            disabled={review.isPending || latest.is_generating}
            onClick={() =>
              review.mutate(undefined, { onSuccess: () => setSelectedId(null) })
            }
          >
            見直す
          </Button>
        )}
        <RecipientPrintButton
          recipients={letterhead.recipients}
          ariaLabel="計画書を印刷"
          print={(targets) =>
            printReportPages(
              targets.map((recipient) =>
                buildCarePlanPage(plan, letterhead, recipient)
              ),
              carePlanDocument(plan)
            )
          }
        />
      </Stack>
      <ScheduledDateChips
        // 版が替わったら札を作り直す (打ちかけの日付を別の版に持ち込まない)
        key={`scheduledDateChips-${plan.id}`}
        dates={plan.scheduled_dates}
        editable={isLatest}
        onChange={(next) => dates.mutate(next)}
      />
      {plan.is_generating && <MetaText>下書きを作っています</MetaText>}
      {isLatest ? (
        <EditableBody patientId={patientId} plan={plan} />
      ) : (
        <ReadOnlyBody plan={plan} />
      )}
    </Stack>
  )
}

// 最初の版を作る。欄 1 つとボタン 1 つ (欄は任意)。押すと文は back が裏で書く
const FirstPlanComposer: FC<{ patientId: string }> = ({ patientId }) => {
  const [memo, setMemo] = useState('')
  const create = useCreateCarePlan(patientId)
  return (
    <Stack spacing={1.5} sx={{ maxWidth: 720 }}>
      <InputBase
        multiline
        minRows={4}
        value={memo}
        onChange={(e) => setMemo(e.target.value)}
        placeholder="医師の指示や聞き取ったこと（任意）"
        inputProps={{ 'aria-label': '医師の指示や聞き取ったこと' }}
        sx={{ ...fieldInputSx, alignItems: 'flex-start' }}
      />
      <Box>
        <Button
          variant="contained"
          disabled={create.isPending}
          onClick={() => create.mutate(memo)}
        >
          下書きを作る
        </Button>
      </Box>
    </Stack>
  )
}

// 計画書のページ (/homecare/patients/:patientId/plan)。design-book ADR-0058。
// 版は見直すたびに増え、直せるのは最新の版だけ。前の版は select で読む。
// 在宅の一覧から外した人は版を増やさない (［見直す］と最初の下書きを出さない。今の版は直せる)。
const HomecarePlanPage: FC = () => {
  const { patientId } = useParams<{ patientId: string }>()
  const navigate = useNavigate()
  const { data, isLoading } = useCarePlans(patientId ?? null)
  const patient = useHomecarePatient(patientId ?? null)
  const ended = patient.data?.ended ?? false
  const name = data?.letterhead.patient.name ?? ''

  return (
    <Layout breadcrumb={['在宅', name, '計画書']}>
      <Box sx={{ mb: 1 }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/homecare/patients/${patientId}`)}
        >
          {name}
        </Button>
      </Box>
      {isLoading || patient.isLoading || !patientId ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      ) : data ? (
        data.plans.length > 0 ? (
          <PlanView
            patientId={patientId}
            plans={data.plans}
            letterhead={data.letterhead}
            ended={ended}
          />
        ) : ended ? (
          <MetaText>在宅の一覧から外しています</MetaText>
        ) : (
          <FirstPlanComposer patientId={patientId} />
        )
      ) : (
        <Typography
          sx={{
            fontSize: scaledFontSize('bodySm'),
            color: colorTokens.fg.muted,
          }}
        >
          在宅の患者が見つかりません。
        </Typography>
      )}
    </Layout>
  )
}

export default HomecarePlanPage
